import React, { useState } from 'react';
import { ListOrdered, ChevronUp, ChevronDown, X, Plus, Music, BookOpen, FileText } from 'lucide-react';

const KIND_ICON = { song: Music, scripture: BookOpen, text: FileText };

/**
 * Run-sheet UI: pick a plan, see its ordered items, reorder/remove them,
 * add text slides, and step Prev/Next. Staging itself is done by the parent
 * through onStage so the existing stage/live pipeline is reused.
 */
const RunSheetPanel = ({ sheet, onStage, onSendLive, onPrepareOffline, dm, t }) => {
  const [open, setOpen] = useState(true);
  const [prep, setPrep] = useState(null); // null | { busy } | { total, cached, failed }
  const [showText, setShowText] = useState(false);
  const [textTitle, setTextTitle] = useState('');
  const [textBody, setTextBody] = useState('');

  const { plans, plan, items, currentIndex } = sheet;

  const submitText = () => {
    if (!textTitle.trim() && !textBody.trim()) return;
    sheet.addItem({ kind: 'text', title: textTitle.trim() || 'Slide', content: textBody.trim() });
    setTextTitle('');
    setTextBody('');
    setShowText(false);
  };

  const hasScripture = items.some(i => i.kind === 'scripture');
  const prepare = async () => {
    setPrep({ busy: true });
    try {
      setPrep(await onPrepareOffline());
    } catch {
      setPrep({ total: 0, cached: 0, failed: [], error: true });
    }
  };

  const step = (dir) => {
    const item = dir > 0 ? sheet.next() : sheet.prev();
    if (item) onStage(item);
  };

  const btn = 'px-2 py-1 rounded-lg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed';
  const input = `w-full rounded-lg px-2 py-1 text-xs border ${dm ? 'bg-slate-800 border-slate-600 text-slate-100' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <div className={`rounded-2xl p-4 border shadow-lg ${t.textPrimary} ${dm ? 'bg-slate-900/60 border-slate-600/40' : 'bg-white border-gray-200'}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-base font-bold"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2"><ListOrdered className="w-4 h-4" /> Run-sheet</span>
        <span className="text-xs font-normal opacity-70">{items.length} item{items.length === 1 ? '' : 's'}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-1">
            <select
              aria-label="Select plan"
              value={plan.id}
              onChange={e => sheet.selectPlan(e.target.value)}
              className={input}
            >
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button type="button" className={`${btn} bg-slate-600 text-slate-50`} title="New plan"
              onClick={() => { const n = window.prompt('Plan name', 'Sunday Service'); if (n !== null) sheet.createPlan(n.trim()); }}>
              <Plus className="w-3 h-3" />
            </button>
            <button type="button" className={`${btn} bg-slate-600 text-slate-50`} title="Rename plan"
              onClick={() => { const n = window.prompt('Rename plan', plan.name); if (n !== null) sheet.renamePlan(n.trim()); }}>
              Rename
            </button>
            <button type="button" className={`${btn} border border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-600 hover:text-white`} title="Delete plan" aria-label="Delete plan"
              onClick={() => { if (window.confirm(`Delete plan "${plan.name}"?`)) sheet.deletePlan(); }}>
              <X className="w-3 h-3" />
            </button>
          </div>

          {items.length === 0 ? (
            <p className="text-xs opacity-60 py-2">
              Empty. Use “Add to run-sheet” on a song or verse, or add a text slide below.
            </p>
          ) : (
            <ol className="space-y-1 max-h-56 overflow-y-auto">
              {items.map((item, i) => {
                const Icon = KIND_ICON[item.kind] || FileText;
                const active = i === currentIndex;
                return (
                  <li
                    key={item.id}
                    className={`flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm border ${active
                      ? 'bg-amber-500/20 border-amber-400'
                      : (dm ? 'border-slate-700' : 'border-gray-200')}`}
                  >
                    <button
                      type="button"
                      className="flex-1 min-w-0 flex items-center gap-1.5 text-left"
                      onClick={() => { sheet.setCurrentIndex(i); onStage(item); }}
                      aria-current={active ? 'true' : undefined}
                    >
                      <span className="opacity-60 w-4 text-right">{i + 1}</span>
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{item.kind === 'scripture' ? item.reference : item.title}</span>
                    </button>
                    <button type="button" aria-label="Move up" title="Move up" className="p-1 rounded hover:bg-slate-500/30 opacity-70 hover:opacity-100 disabled:opacity-20"
                      disabled={i === 0} onClick={() => sheet.moveItem(item.id, -1)}><ChevronUp className="w-4 h-4" /></button>
                    <button type="button" aria-label="Move down" title="Move down" className="p-1 rounded hover:bg-slate-500/30 opacity-70 hover:opacity-100 disabled:opacity-20"
                      disabled={i === items.length - 1} onClick={() => sheet.moveItem(item.id, 1)}><ChevronDown className="w-4 h-4" /></button>
                    <button type="button" aria-label="Remove" title="Remove from the run-sheet" className="p-1 rounded opacity-70 hover:opacity-100 hover:bg-red-600 hover:text-white"
                      onClick={() => sheet.removeItem(item.id)}><X className="w-4 h-4" /></button>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="flex gap-1 flex-wrap">
            <button type="button" className={`${btn} bg-slate-600 text-slate-50`} disabled={!sheet.canPrev} onClick={() => step(-1)}>← Prev</button>
            <button type="button" className={`${btn} bg-amber-600 text-slate-50`} disabled={!sheet.canNext} onClick={() => step(1)}>Next →</button>
            <button type="button" className={`${btn} bg-green-600 text-slate-50`} onClick={onSendLive}>Live ⏎</button>
            <button type="button" className={`${btn} bg-slate-600 text-slate-50 ml-auto`} onClick={() => setShowText(s => !s)}>+ Text slide</button>
          </div>

          {showText && (
            <div className="space-y-1">
              <input className={input} placeholder="Title (e.g. Announcements)" value={textTitle} onChange={e => setTextTitle(e.target.value)} />
              <textarea className={input} rows={3} placeholder="Slide text" value={textBody} onChange={e => setTextBody(e.target.value)} />
              <button type="button" className={`${btn} bg-emerald-600 text-slate-50`} onClick={submitText}>Add slide</button>
            </div>
          )}
          {onPrepareOffline && hasScripture && (
            <div className="space-y-1">
              <button
                type="button"
                className={`${btn} bg-slate-600 text-slate-50`}
                onClick={prepare}
                disabled={!!(prep && prep.busy)}
                title="Download the verses in this plan now so they work if the connection drops"
              >
                {prep && prep.busy ? 'Preparing…' : 'Prepare offline'}
              </button>
              {prep && !prep.busy && (
                <p role="status" className="text-[11px]">
                  {prep.error
                    ? 'Could not prepare. Check your connection and try again.'
                    : prep.failed.length === 0
                      ? `Ready: ${prep.cached} verse${prep.cached === 1 ? '' : 's'} saved for offline.`
                      : `${prep.cached} of ${prep.total} saved. Could not load: ${prep.failed.join(', ')}.`}
                </p>
              )}
            </div>
          )}
          <p className="text-xs opacity-60">Keys: ← → item · ↑ ↓ slide · Enter go live · B clear · ? all shortcuts</p>
        </div>
      )}
    </div>
  );
};

export default RunSheetPanel;
